import HBIcon from "@/components/HBIcon";
import { EVENT } from "@/constants/event";
import useAppStore from "@/context/store";
import { RelationWords } from "@/pageComponents/Search/RelationWords";
import useSideNavCollapse from "@/utils/useSideNavCollapse";
import { Badge, Button } from "antd";
import classNames from "classnames";
import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";
import styles from "./index.module.less";

const FilterToggleButton = ({ relationList }) => {
  const [isCollapsed] = useSideNavCollapse();
  const { isFilterVisible, setFilterVisible, getSelectedCount } = useAppStore(
    (state) => ({
      isFilterVisible: state.isFilterVisible,
      setFilterVisible: state.setFilterVisible,
      getSelectedCount: state.getSelectedCount,
    })
  );

  const timerRef = useRef(null);
  const [isFixed, setIsFixed] = useState(false);
  const containerRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const onResize = debounce((e) => {
      // 只在侧边栏展开且筛选面板打开时，延迟关闭
      if (e && !e.detail) {
        timerRef.current = setTimeout(() => {
          setFilterVisible(false);
        }, 60);
      }

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }, 300);

    onResize();

    window.addEventListener(EVENT.SIDBAR_COLLAPSED, onResize);

    return () => {
      window.removeEventListener(EVENT.SIDBAR_COLLAPSED, onResize);
    };
  }, [isCollapsed]); // 只在 isCollapsed 变化时触发

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--filter-panel-width",
      isFilterVisible ? "192px" : "0px"
    );
  }, [isFilterVisible]);

  useEffect(() => {
    const headerHeight =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--total-header-height"
        )
      ) || 60;

    // 创建一个占位元素来观察
    const placeholder = document.createElement("div");
    placeholder.style.height = "1px";
    placeholder.style.position = "absolute";
    placeholder.style.top = "0";
    placeholder.style.left = "0";
    placeholder.style.right = "0";
    placeholder.style.pointerEvents = "none";

    if (containerRef.current) {
      containerRef.current.parentNode.insertBefore(
        placeholder,
        containerRef.current
      );
    }

    // 创建观察者
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // 当占位元素可见时，说明需要还原
        if (entry.isIntersecting) {
          setIsFixed(false);
        } else {
          // 当占位元素不可见时，检查是否需要吸顶
          const containerRect = containerRef.current.getBoundingClientRect();
          if (containerRect.top <= headerHeight) {
            setIsFixed(true);
          }
        }
      },
      {
        threshold: [0, 1],
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
      }
    );

    // 开始观察占位元素
    observerRef.current.observe(placeholder);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (placeholder && placeholder.parentNode) {
        placeholder.parentNode.removeChild(placeholder);
      }
    };
  }, []);

  const handleClick = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setFilterVisible(!isFilterVisible);
  };

  const selectedCount = getSelectedCount();

  return (
    <div className={styles.wrapper}>
      <div
        ref={containerRef}
        className={classNames([
          `${styles.filterToggleButtonContainer} ${
            isFixed ? (isCollapsed ? styles.fixedCollapsed : styles.fixed) : ""
          }`,
        ])}
      >
        <div className={styles.badgeWrapper}>
          <Button
            data-gd-click="button_click"
            data-button-name={`筛选`}
            data-content-source="搜索"
            className={classNames(styles.filterToggleButton, {
              [styles.active]: isFilterVisible,
            })}
            onClick={handleClick}
          >
            <HBIcon type="a-hb_filter2" className={styles.filterIcon} />
            筛选
            <Badge
              count={isFilterVisible ? 0 : selectedCount}
              size="default"
            ></Badge>
          </Button>
        </div>
        {relationList?.length > 0 && (
          <div
            className={styles.relationWordsWrapper}
            style={{
              width:
                !isFilterVisible && selectedCount > 0
                  ? `calc(var(--wrapper-width) - 128px)`
                  : `calc(var(--wrapper-width) - 102px)`,
            }}
          >
            <RelationWords relationList={relationList} />
          </div>
        )}
      </div>
      {isFixed && <div className={styles.placeholder} />}
    </div>
  );
};

export default FilterToggleButton;
